import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
import { OBJExporter } from 'three/addons/exporters/OBJExporter.js';
import { STLExporter } from 'three/addons/exporters/STLExporter.js';
import type { ModelDocument } from '../model/types';
import { downloadBlob } from '../utils/fileIO';
import { createDocumentRoot, disposeObject } from './entityObjects';

export type ExportFormat = 'glb' | 'obj' | 'stl';

function safeName(name: string): string {
  return name.trim().replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '') || 'constructcanvas-model';
}

export async function exportDocument(document: ModelDocument, format: ExportFormat): Promise<void> {
  const root = createDocumentRoot(document, { includeEdges: false, forExport: true });
  root.updateMatrixWorld(true);
  const fileBase = safeName(document.name);

  try {
    if (format === 'obj') {
      const output = new OBJExporter().parse(root);
      downloadBlob(new Blob([output], { type: 'text/plain' }), `${fileBase}.obj`);
      return;
    }

    if (format === 'stl') {
      const output = new STLExporter().parse(root, { binary: true });
      const bytes = output instanceof DataView
        ? new Uint8Array(output.buffer, output.byteOffset, output.byteLength).slice()
        : new TextEncoder().encode(output);
      downloadBlob(new Blob([bytes], { type: 'model/stl' }), `${fileBase}.stl`);
      return;
    }

    const output = await new Promise<ArrayBuffer>((resolve, reject) => {
      new GLTFExporter().parse(
        root,
        (result) => {
          if (result instanceof ArrayBuffer) resolve(result);
          else resolve(new TextEncoder().encode(JSON.stringify(result)).buffer);
        },
        (error) => reject(error),
        { binary: true, onlyVisible: true, truncateDrawRange: true },
      );
    });
    downloadBlob(new Blob([output], { type: 'model/gltf-binary' }), `${fileBase}.glb`);
  } finally {
    disposeObject(root);
  }
}
